import { Component, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ActionsAnalyticsService, FirebaseUserProfileService, IPhoneDetails, IPhoneProduct, IUser, IUserPlan, UserPlansService } from '@ztarmobile/zwp-service-backend';
import { combineLatest, takeWhile } from 'rxjs/operators';
import { ACCOUNT_ROUTE_URLS, ACTIVATION_ROUTE_URLS, ROUTE_URLS, SHOP_ROUTE_URLS } from 'src/app/app.routes.names';
import { AppState } from 'src/app/app.service';
import { MetaService } from 'src/services/meta-service.service';

@Component({
  selector: 'app-checkout-result',
  templateUrl: './checkout-result.component.html',
  styleUrls: ['./checkout-result.component.scss']
})

export class CheckoutResultComponent implements OnDestroy {
  public selectedUserPlan: IUserPlan;
  public userPlanId: string;
  public isChangePlanSummary = false;
  public nextCycle = false;
  public trustedUrl;
  public isPlanAddon = false;
  public isOrderSim = false;
  public purchasedPlanId: string;
  public purchasedPlan: IUserPlan;
  public isMoneySavingReferral: boolean;
  public utmContent: string;
  public phone: IPhoneDetails;
  public selectedProduct: IPhoneProduct;
  public deviceImageURL: string;
  public user: IUser;
  public isSuccessfulMultiplePurchases: string;
  public subHeaderTitle: string = '';
  public orderId: any;
  public isActivationFlow: boolean;
  
  private alive = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userPlansService: UserPlansService,
    private appState: AppState,
    private metaService: MetaService,
    private userProfileService: FirebaseUserProfileService,
    private analyticsService: ActionsAnalyticsService) {

    this.userProfileService.userProfileObservable.pipe(takeWhile(() => this.alive)).subscribe((user) => this.user = user);
    this.appState.clearSessionStorage();
    this.appState.loading = true;
    this.userPlansService.isSelectedPlanReady.pipe(combineLatest(this.route.params, (ready, params: Params) => {
      const nextCycleParam = params[SHOP_ROUTE_URLS.PARAMS.CHANGE_NEXT_CYCLE];
      this.isChangePlanSummary = nextCycleParam === 'false' || nextCycleParam === 'true';
      this.nextCycle = nextCycleParam === 'true';
      this.userPlanId = params[ROUTE_URLS.PARAMS.USER_PLAN_ID];
      const planAddonParam = params[SHOP_ROUTE_URLS.PARAMS.ADD_ON_PLAN];
      if (!!params[ACTIVATION_ROUTE_URLS.PARAMS.ACTIVATION]) {
        this.isActivationFlow = true;
      }
      this.isPlanAddon = planAddonParam === 'true' || planAddonParam === 'false';
      this.selectedUserPlan = this.userPlansService.selectedUserPlan;
      this.purchasedPlanId = params[ROUTE_URLS.PARAMS.SELECTED_PLAN];
      const orderSimParam = params[SHOP_ROUTE_URLS.PARAMS.ORDER_SIM];
      this.isOrderSim = orderSimParam === 'true' || orderSimParam === 'false';
      const successfulPurchases = params[SHOP_ROUTE_URLS.PARAMS.SUCCESSFUL_PURCHASE];
      this.isSuccessfulMultiplePurchases = successfulPurchases;
      this.orderId = params[ROUTE_URLS.PARAMS.USER_ORDER_ID];
      if (this.isChangePlanSummary && !this.selectedUserPlan.mdn) {
        this.goToAccountSummary();
      }
      const planID = !!this.userPlanId ? this.userPlanId : this.purchasedPlanId;
      if (!!planID) {
        setTimeout(() => {
          this.appState.loading = true;
          this.userPlansService.userPlans.pipe(takeWhile(() => this.alive)).subscribe((plans) => {
            this.appState.loading = false;
            this.purchasedPlan = plans.find((plan) => plan.id === planID);
            this.getSubHeaderTitle();
            // if (!!this.isOrderSim && !!planID && !!this.purchasedPlan) { //TODO: remove this code when bff fixes the issue of not setting this property in firebase
            //   this.purchasedPlan.planDevice.pendingNewSim = true;
            //   this.userPlansService.updateUserPlan(this.purchasedPlan.userId, this.purchasedPlan)
            // }
          });
        }, 200);
      } else {
        this.getSubHeaderTitle();
      }
    }), takeWhile(() => this.alive)).subscribe();
    this.appState.loading = false;
    this.appState.isMoneySavingProCampaign.pipe(takeWhile(() => this.alive)).subscribe((result) => {
      if (!!result) {
        this.isMoneySavingReferral = true;
        this.utmContent = sessionStorage.getItem('MoneyProContent');
        this.trustedUrl = 'https://services.moneysavingpro.com/v1/conversion/?pid=834&cid=' + this.utmContent;
        this.analyticsService.trackMoneySavingProClick(this.utmContent);
        sessionStorage.setItem('MoneyProReferral', 'false');
        sessionStorage.removeItem('MoneyProContent');
      } else {
        this.isMoneySavingReferral = false;
      }
    });
    this.metaService.createCanonicalUrl();
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  public getSubHeaderTitle(): void {
    if (!this.isChangePlanSummary && !this.isPlanAddon && !this.isOrderSim && !this.isSuccessfulMultiplePurchases && !this.purchasedPlan?.eSIM) {
      this.subHeaderTitle = 'Your FreeUp plan has been purchased.';
    } else if (!this.isChangePlanSummary && !this.isPlanAddon && !this.isOrderSim && !this.isSuccessfulMultiplePurchases && !!this.purchasedPlan?.eSIM) {
      this.subHeaderTitle = 'Your FreeUp plan & eSIM has been purchased.';
    } else if (!!this.isOrderSim && !this.isPlanAddon && !this.isChangePlanSummary && !this.purchasedPlan?.eSimDetails) {
      this.subHeaderTitle = 'Your Replacement SIM has been purchased!';
    } else if (!!this.isOrderSim && !this.isPlanAddon && !this.isChangePlanSummary && !!this.purchasedPlan?.eSimDetails) {
      this.subHeaderTitle = 'Your Replacement eSIM has been purchased!';
    } else if (!!this.isPlanAddon && !this.isChangePlanSummary && !this.isOrderSim) {
      this.subHeaderTitle = 'Your Plan add-on have been purchased!';
    } else if (!!this.isSuccessfulMultiplePurchases && this.isSuccessfulMultiplePurchases === 'both' && !this.purchasedPlan?.eSimDetails) {
      this.subHeaderTitle = 'Your Replacement SIM + Plan add-on have been purchased!';
    } else if (!!this.isSuccessfulMultiplePurchases && this.isSuccessfulMultiplePurchases === 'both' && !!this.purchasedPlan?.eSimDetails) {
      this.subHeaderTitle = 'Your Replacement eSIM + Plan add-on has been purchased!';
    } else if (!!this.isSuccessfulMultiplePurchases && this.isSuccessfulMultiplePurchases === 'addon') {
      this.subHeaderTitle = 'New SIM purchase failed!';
    }
  }

  public goToOrderDetail(orderId: string): void {
    const params = {};
    params[ACCOUNT_ROUTE_URLS.PARAMS.ORDER_ID] = orderId;
    this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.ORDER_DETAILS}`, params]);
  }

  public goToHome(): void {
    this.router.navigate([`${ROUTE_URLS.HOME}`]);
  }

  public goToAccountSummary(): void {
    const params = {};
    params[SHOP_ROUTE_URLS.PARAMS.SUCCESSFUL_PURCHASE] = true;
    this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.SUMMARY}`, params]);
  }

  public goToPendingActivation(): void {
    this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.PENDING_ACTIVATIONS}`]);
  }

  public goToActivation(): void {
    const params = {};
    params[ROUTE_URLS.PARAMS.USER_PLAN_ID] = this.userPlanId;
    this.router.navigate([`${ACTIVATION_ROUTE_URLS.BASE}/${ACTIVATION_ROUTE_URLS.CHOOSE_ACTIVATION_PATH}`, params]);
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event): void {
    event.preventDefault();
    sessionStorage.setItem('purchased', 'true');
  }

}
